import { useAlarmV2Rpt, useExportAlarmRpt } from '@services/report'
import Flex from '@shared/Flex'
import { Button, Input } from '@shared/Form'
import { RangePicker } from '@shared/Form/DatePicker'
import useLocationSearch from '@shared/innmaxLib/hooks/useLocationSearch'
import useSearch from '@shared/innmaxLib/hooks/useSearch'
import Tooltip from '@shared/innmaxUI/Tooltip'
import Layout from '@shared/Layout'
import Table from '@shared/Table'
import { downloadFile } from '@shared/utils/webHelper'
import { Form } from 'antd'
import { ColumnsType } from 'antd/es/table'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { FormItem } from 'src/components/Form'
import OptionsSelect from 'src/components/OptionsSelect'

function AlarmV2() {
  const { t } = useTranslation()
  const history = useHistory()
  const queryParams = useLocationSearch()

  const [form] = Form.useForm()

  const { query, handleSearch, handleTableChange } = useSearch({
    form,
    keepPreviousQuery: false,
    dateRangeField: ['startAt', 'endAt'],
    transform: values => {
      const params = {
        ...values,
        deep: true,
      }
      return params
    },
  })

  // 處理URL參數中的input值
  useEffect(() => {
    if (queryParams.input) {
      // 設定表單的input欄位值
      form.setFieldsValue({
        input: queryParams.input,
      })
      // 執行搜尋
      setTimeout(() => {
        handleSearch()
        // 清空URL參數
        history.replace(history.location.pathname)
      }, 100)
    }
  }, [queryParams.input, form, handleSearch, history])

  const { loading, dataSource } = useAlarmV2Rpt(query)

  const [exporting, handleExport] = useExportAlarmRpt(query, {
    onSuccess: (blob: any) => {
      downloadFile({
        blob,
        filename: 'report',
      })
    },
  })

  const columns: ColumnsType<any> = [
    {
      title: '警報時間',
      dataIndex: 'createAtStr',
      align: 'center',
    },
    {
      title: '設備編號',
      dataIndex: 'deviceId',
      align: 'center',
    },
    {
      title: '槍號',
      dataIndex: ['extra', 'connectorDeviceId'],
      align: 'center',
      render: (v, r) => (!!v ? v : '-'),
    },
    {
      title: '設備種類',
      dataIndex: 'deviceTypeStr',
      width: '150',
      className: 'text-center',
    },
    {
      title: '警報等級',
      dataIndex: 'isPopUp',
      width: '150',
      className: 'text-center',
      render: (v, r) => (
        <span style={v ? { color: '#E17567' } : { color: '#fff' }}>
          {v ? '嚴重' : '一般'}
        </span>
      ),
    },
    {
      title: '縣市',
      dataIndex: 'cityStr',
      align: 'center',
    },
    {
      title: '行政區',
      dataIndex: 'districtStr',
      align: 'center',
    },
    {
      title: '站點',
      dataIndex: 'parkingLotStr',
      align: 'center',
    },
    {
      title: '警報種類',
      dataIndex: 'alarmTypeDesc',
      align: 'center',
      render: (v, r) => (
        <Tooltip placement="top" title={v}>
          {v}
        </Tooltip>
      ),
    },
    {
      title: '警報事件描述',
      dataIndex: 'extra',
      width: '150',
      align: 'center',
      render: ({ vendorErrorCode, description, info }) =>
        vendorErrorCode || description || info
          ? `${vendorErrorCode || ''}, ${description || info || ''}`
          : '-',
    },
    {
      title: '備註',
      dataIndex: ['extra', 'action'],
      width: '350',
      ellipsis: true,
      className: 'text-center',
      render: v => (
        <Tooltip placement="top" title={v}>
          {v || '-'}
        </Tooltip>
      ),
    },
  ]

  return (
    <>
      <Layout>
        <Form layout="inline" form={form}>
          <FormItem
            name="input"
            label={t('device:search keyword')}
            className="w-264">
            <Input allowClear placeholder={'搜尋設備或站點'} />
          </FormItem>

          <FormItem label={'行政區'} name="sessionId" className="w-280">
            <OptionsSelect.CityAreaSelect className="w-130" />
          </FormItem>

          <FormItem name="alarmType" label="警報種類" className="w-144">
            <OptionsSelect.AlarmTypeSelect />
          </FormItem>
          <FormItem name="date" label={'警報時間'} className="w-264">
            <RangePicker />
          </FormItem>

          <FormItem.Button>
            <Button
              loading={false}
              type="primary"
              className="w-96"
              onClick={handleSearch}>
              {t('common:search')}
            </Button>
          </FormItem.Button>
          <FormItem.Button>
            <Button
              loading={exporting}
              type="primary"
              ghost
              className="w-96"
              onClick={handleExport}>
              {t('report:export')}
            </Button>
          </FormItem.Button>
        </Form>

        <div className="mt-44 mb-16">
          <Flex between>
            <Table.RowCount count={dataSource.total} />
          </Flex>
        </div>
        <Table
          columns={columns}
          loading={loading}
          dataSource={dataSource}
          onChange={handleTableChange}
        />
      </Layout>
    </>
  )
}

export default React.memo(AlarmV2)
